import { connectGuideHelp, createGuideHelp, setGuideValueUnit } from '@aknvas/size-guide-help';
import { placeTooltip } from '@aknvas/size-guide-tooltip';
import { validateFitProfile, formatFitRange } from '@aknvas/fit-profile';
import { normalizeGuide, formatMeasurement } from '@aknvas/size-guide-data';
import { connectSizeOption } from '@aknvas/size-guide-variant';
import { connectReferenceChart, highlightReferenceSize, syncReferenceUnits, presentReferenceHelp, labelReferenceValues, formatReferenceCopy } from '@aknvas/size-guide-reference';
import { lockScroll, unlockScroll } from '@theme/utilities';

class AknvasSizeGuide extends HTMLElement {
  #abort;
  #adapter;
  #model;
  #data;
  #dialog;
  #size;
  #unit = 'in';
  #diagram;
  #loadingDiagram;
  #returnFocus;
  #tipTarget;
  #tipHideTimer;
  #pinnedTip;
  #fit;
  #referenceController;
  #referenceState = 'loading';

  connectedCallback() {
    if (this.#abort) return;
    try {
      this.#data = JSON.parse(this.querySelector('[data-guide-data]').textContent);
    } catch {
      this.hidden = true;
      return;
    }
    try {
      this.#model = normalizeGuide(this.#data.specs, this.#data.sizes);
    } catch {
      // Chart-only products still use the same dialog; no garment values are fabricated.
      const sizes = Array.isArray(this.#data.sizes) ? this.#data.sizes : [];
      this.#model = { rows: [], sizes: [...new Set(sizes.map(size => String(size).trim()).filter(Boolean))] };
    }
    try {
      this.#fit = validateFitProfile(this.#data.fitProfile, this.#model.sizes, { publicPayload: true });
    } catch {
      this.#fit = null;
    }
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#dialog = this.querySelector('dialog');
    const heading = this.querySelector('h2');
    heading.id = `aknvas-guide-${crypto.randomUUID()}`;
    this.#dialog.setAttribute('aria-labelledby', heading.id);
    this.#adapter = connectSizeOption(this, this.#data, (size) => {
      if (!this.#model.sizes.includes(size)) return;
      this.#size = size;
      this.#render();
    });
    this.#size = this.#model.sizes.includes(this.#adapter.read()) ? this.#adapter.read() : this.#model.sizes[0];
    this.querySelector('[data-guide-diagram]').hidden = !this.#data.svgUrl;
    const sizes = this.querySelector('[data-guide-sizes]');
    sizes.replaceChildren(...this.#model.sizes.map((size) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.guideSize = size;
      button.textContent = size;
      button.addEventListener('click', () => {
        this.#size = size;
        this.#adapter.select(size);
        this.#render();
      }, options);
      return button;
    }));
    this.#dialog.addEventListener('click', (event) => {
      if (event.composedPath().some((node) => node === this.#tipTarget || node === this.querySelector('[data-guide-tip]'))) return;
      this.#pinnedTip = null;
      this.#hideTip();
    }, options);
    const tip = this.querySelector('[data-guide-tip]');
    tip.addEventListener('pointerenter', () => clearTimeout(this.#tipHideTimer), options);
    tip.addEventListener('pointerleave', this.#queueHideTip, options);
    this.#dialog.addEventListener('cancel', (event) => {
      if (!tip.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.#hideTip();
      }
    }, options);
    this.#dialog.addEventListener('scroll', this.#hideTip, options);
    window.addEventListener('resize', this.#hideTip, options);
    this.querySelector('[data-guide-open]').addEventListener('click', this.#open, options);
    this.querySelector('[data-guide-close]').addEventListener('click', () => this.#dialog.close(), options);
    this.#dialog.addEventListener('close', () => {
      this.#pinnedTip = null;
      this.#hideTip();
      unlockScroll(this.#dialog);
      if (this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
    }, options);
    this.#dialog.addEventListener('click', (event) => {
      if (event.target !== this.#dialog) return;
      const box = this.#dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.#dialog.close();
    }, options);
    for (const button of this.querySelectorAll('[data-guide-unit]')) {
      button.addEventListener('click', () => {
        this.#unit = button.dataset.guideUnit;
        this.#render();
      }, options);
    }
    connectGuideHelp(this.#dialog, this.#abort.signal);
    this.querySelector('[data-guide-reference-retry]').addEventListener('click', () => this.#referenceController?.retry(), options);
    this.#referenceController = connectReferenceChart(this, (state) => {
      this.#referenceState = state;
      this.#render();
    });
    this.#render();
  }

  disconnectedCallback() {
    this.#hideTip();
    this.#abort?.abort();
    this.#abort = null;
    this.#adapter?.disconnect();
    this.#referenceController?.disconnect();
    if (this.#dialog) {
      if (this.#dialog.open) this.#dialog.close();
      unlockScroll(this.#dialog);
    }
    this.#diagram = null;
    this.#loadingDiagram = false;
    this.#returnFocus = null;
    this.querySelector('[data-guide-diagram]')?.shadowRoot?.replaceChildren();
  }

  #open = () => {
    this.#returnFocus = document.activeElement;
    this.#dialog.showModal();
    lockScroll(this.#dialog);
    this.#loadDiagram();
    if (this.#referenceState === 'error') this.#referenceController?.retry();
  };

  async #loadDiagram() {
    if (this.#diagram || this.#loadingDiagram || !this.#data.svgUrl || !this.#model.rows.length) return;
    this.#loadingDiagram = true;
    const signal = this.#abort.signal;
    const container = this.querySelector('[data-guide-diagram]');
    const fallback = this.querySelector('[data-guide-diagram-status]');
    container.hidden = false;
    fallback.hidden = false;
    fallback.textContent = this.#data.labels.loading;
    try {
      const { loadDiagram } = await import('@aknvas/size-guide-svg');
      if (signal.aborted) return;
      const diagram = await loadDiagram(container, this.#data.svgUrl, this.#model.rows, signal, this.#tip, this.#data.labels.diagram);
      if (signal.aborted) return;
      this.#diagram = diagram;
      fallback.hidden = true;
      this.#render();
    } catch (error) {
      if (signal.aborted) return;
      container.shadowRoot?.replaceChildren();
      container.hidden = true;
      this.querySelector('[data-guide-measurements]').classList.remove('visually-hidden');
      fallback.textContent = this.#data.labels.diagramError;
    } finally {
      if (!signal.aborted) this.#loadingDiagram = false;
    }
  }

  #queueHideTip = () => {
    clearTimeout(this.#tipHideTimer);
    if (!this.#pinnedTip) this.#tipHideTimer = setTimeout(this.#hideTip, 120);
  };

  #hideTip = () => {
    clearTimeout(this.#tipHideTimer);
    const tip = this.querySelector('[data-guide-tip]');
    if (tip) tip.hidden = true;
    this.#tipTarget = null;
    this.#pinnedTip = null;
  };

  #tip = (row, target, action) => {
    if (action === 'leave') {
      this.#queueHideTip();
      return;
    }
    clearTimeout(this.#tipHideTimer);
    if (action === 'toggle') {
      if (this.#pinnedTip === target) {
        this.#pinnedTip = null;
        this.#hideTip();
        return;
      }
      this.#pinnedTip = target;
    } else if (this.#pinnedTip && this.#pinnedTip !== target) return;
    this.#tipTarget = target;
    const tip = this.querySelector('[data-guide-tip]');
    this.querySelector('[data-guide-tip-title]').textContent = row.label;
    this.querySelector('[data-guide-tip-copy]').textContent = row.tip;
    tip.hidden = false;
    const frame = this.querySelector('.aknvas-size-guide__illustration').getBoundingClientRect();
    const anchor = target.getBoundingClientRect();
    const box = tip.getBoundingClientRect();
    const position = placeTooltip(anchor, frame, box);
    tip.style.left = `${position.left}px`;
    tip.style.top = `${position.top}px`;
    tip.style.setProperty('--arrow-offset', `${position.arrow}px`);
    tip.dataset.placement = position.placement;
  };

  #render() {
    const locale = document.documentElement.lang || 'en';
    const hasGarment = this.#model.rows.length > 0;
    const unitLabel = this.#data.labels.valueUnits?.[this.#unit] ?? this.#unit;
    const reference = this.querySelector('[data-guide-reference]');
    const hasReference = Boolean(reference?.querySelector('table'));
    const expectsReference = this.dataset.guideReferenceExpected === 'true';
    this.hidden = !hasGarment && !this.#fit && !hasReference && !expectsReference;
    this.toggleAttribute('data-chart-only', !hasGarment);
    this.querySelector('.aknvas-size-guide__illustration').hidden = !hasGarment;
    this.querySelector('.aknvas-size-guide__note').hidden = !hasGarment;
    this.querySelector('.aknvas-size-guide__units').hidden = !hasGarment && !this.#fit && !hasReference && !expectsReference;
    for (const button of this.querySelectorAll('[data-guide-size]')) {
      button.setAttribute('aria-pressed', String(button.dataset.guideSize === this.#size));
      button.toggleAttribute('data-unavailable', !this.#adapter.available(button.dataset.guideSize));
    }
    for (const button of this.querySelectorAll('[data-guide-unit]')) button.setAttribute('aria-pressed', String(button.dataset.guideUnit === this.#unit));
    this.querySelector('[data-guide-selection-status]').textContent = this.#size ? this.#data.labels.showingSize.replace('__SIZE__', this.#size) : '';
    const measurements = this.querySelector('[data-guide-measurements]');
    const fallbackVisible = !this.#data.svgUrl || this.querySelector('[data-guide-diagram]').hidden;
    measurements.classList.toggle('visually-hidden', !fallbackVisible);
    measurements.replaceChildren(...this.#model.rows.flatMap((row) => {
      const name = document.createElement('dt'); name.textContent = row.label;
      const value = document.createElement('dd');
      const measurement = row.measurements.get(this.#size);
      value.textContent = formatMeasurement(measurement, this.#unit, locale, false);
      setGuideValueUnit(value, measurement ? unitLabel : null);
      if (row.tip && fallbackVisible) {
        const copy = document.createElement('p'); copy.textContent = row.tip;
        name.append(createGuideHelp(this.#data.labels.measurementHelp?.replace('__MEASUREMENT__', row.label) ?? row.label, copy));
      }
      return [name, value];
    }));
    const fitRows = this.#fit?.sizes.get(this.#size);
    if (reference) {
      reference.hidden = (!hasReference && !expectsReference) || Boolean(fitRows);
      const failed = this.#referenceState === 'error';
      const status = this.querySelector('[data-guide-reference-status]');
      status.hidden = hasReference || !expectsReference;
      const statusText = failed ? this.#data.labels.referenceError : this.#data.labels.referenceLoading;
      // The provider observer watches this section; unchanged text must not
      // create another child-list mutation and restart the render cycle.
      if (status.textContent !== statusText) status.textContent = statusText;
      this.querySelector('[data-guide-reference-recovery]').hidden = hasReference || !expectsReference || !failed;
      presentReferenceHelp(reference, this.#data.labels, locale);
      syncReferenceUnits(reference, this.#unit, locale, this.#data.labels.referenceUnits);
      labelReferenceValues(reference, unitLabel);
      highlightReferenceSize(reference, this.#size);
    }
    this.querySelector('[data-guide-fit]').hidden = !fitRows;
    if (fitRows) {
      this.querySelector('[data-guide-fit-note]').textContent = formatReferenceCopy(this.#fit.note, this.#unit, locale, this.#data.labels.referenceUnits);
      const help = this.querySelector('[data-guide-fit-help]');
      if (help) help.hidden = !this.#fit.note;
      this.querySelector('[data-guide-fit-ranges]').replaceChildren(...fitRows.flatMap((row) => {
        const name = document.createElement('dt'); name.textContent = this.#data.labels.fitDimensions[row.key];
        const value = document.createElement('dd'); value.textContent = formatFitRange(row, this.#fit.unit, this.#unit, locale, false);
        setGuideValueUnit(value, unitLabel);
        return [name, value];
      }));
    }
    this.#hideTip();
    this.#diagram?.(this.#size, this.#unit, locale);
  }
}

if (!customElements.get('aknvas-size-guide')) customElements.define('aknvas-size-guide', AknvasSizeGuide);
